import React, { useState, useEffect } from "react";
import "./Navbar.css";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { logout } from "../../Features/Auth/authSlice";
import { RiMenu2Line, RiShoppingBagLine } from "react-icons/ri";
import { FiSearch, FiLogOut, FiHeart } from "react-icons/fi";
import { FaRegUser } from "react-icons/fa6";
import { MdOutlineClose } from "react-icons/md";
import Badge from "@mui/material/Badge";
import LanguageSwitcher from "./LanguageSwitcher";
import { useRef } from "react";

const Navbar = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cart = useSelector((state) => state.cart);
  const { user } = useSelector((state) => state.auth);
  const { wishlistIds } = useSelector((state) => state.wishlist);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    document.body.style.overflow = mobileMenuOpen ? "auto" : "hidden";
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  // Combined function to close menu and scroll to top
  const closeMobileMenuAndScrollToTop = () => {
    setMobileMenuOpen(false);
    document.body.style.overflow = "auto";
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };



  return (
    <>
      <nav className="navBar">
        <div className="logoContainer">
          <Link to="/" onClick={scrollToTop}>
            <img
              src="https://res.cloudinary.com/dlz4nxlct/image/upload/v1745092032/nxh7vgevj23fgals92qn.png"
              alt="Logo"
            />
          </Link>
        </div>
        <div className="linkContainer">
          <ul>
            <li>
              <Link to="/" onClick={scrollToTop}>
                {t('nav.home')}
              </Link>
            </li>
            <li>
              <Link to="/shop" onClick={scrollToTop}>
                {t('nav.shop')}
              </Link>
            </li>
            <li>
              <Link to="/links/" onClick={scrollToTop}>
                {t('nav.links')}
              </Link>
            </li>
          </ul>
        </div>
        <div className="iconContainer">
          <FiSearch size={22} onClick={scrollToTop} />
          <Link to="/cart" onClick={scrollToTop}>
            <Badge
              badgeContent={cart.items.length === 0 ? "0" : cart.items.length}
              color="primary"
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
            >
              <RiShoppingBagLine size={22} />
            </Badge>
          </Link>
          <Link to="/wishlist" onClick={scrollToTop}>
            <Badge
              badgeContent={wishlistIds.length}
              color="primary"
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
            >
              <FiHeart size={22} />
            </Badge>
          </Link>
          {user ? (
            <div className="profile-dropdown-container" ref={dropdownRef}>
              <div
                className="profile-icon-trigger"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              >
                <FaRegUser size={22} />
              </div>
              {isDropdownOpen && (
                <div className="profile-dropdown-menu">
                  <div className="dropdown-user-info">
                    <p className="user-name">{user.first_name} {user.last_name}</p>
                    <p className="user-email">{user.email}</p>
                  </div>
                  <hr />
                  <Link to="/profile" onClick={() => setIsDropdownOpen(false)}>
                    {t('nav.profile')}
                  </Link>
                  <button onClick={handleLogout} className="logout-btn">
                    <FiLogOut size={16} /> {t('nav.logout')}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" onClick={scrollToTop}>
              <FaRegUser size={22} />
            </Link>
          )}
          <LanguageSwitcher />
        </div>
      </nav>

      {/* Mobile Menu */}
      <nav className="mobile-nav-wrapper">
        <div className="mobile-nav">
          <div className="mobile-nav-left">
            {mobileMenuOpen ? (
              <MdOutlineClose size={24} onClick={toggleMobileMenu} />
            ) : (
              <RiMenu2Line size={24} onClick={toggleMobileMenu} />
            )}
          </div>
          <div className="mobile-nav-center">
            <div className="logoContainer">
              <Link to="/" onClick={closeMobileMenuAndScrollToTop}>
                <img
                  src="https://res.cloudinary.com/dlz4nxlct/image/upload/v1745092032/nxh7vgevj23fgals92qn.png"
                  alt="Logo"
                />
              </Link>
            </div>
          </div>
          <div className="mobile-nav-right" style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            {user ? (
              <Link to="/profile" onClick={scrollToTop}>
                <FaRegUser size={24} color="black" />
              </Link>
            ) : (
              <Link to="/login" onClick={scrollToTop}>
                <FaRegUser size={24} color="black" />
              </Link>
            )}
            <Link to="/wishlist" onClick={scrollToTop} aria-label="Wishlist">
              <Badge
                badgeContent={wishlistIds.length}
                color="primary"
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
              >
                <FiHeart size={24} color="black" />
              </Badge>
            </Link>
            <Link to="/cart" onClick={scrollToTop} aria-label="Cart">
              <Badge
                badgeContent={cart.items.length === 0 ? "0" : cart.items.length}
                color="primary"
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
              >
                <RiShoppingBagLine size={24} color="black" />
              </Badge>
            </Link>
          </div>
        </div>
        <div className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`}>
          <div className="mobile-menuTop">
            <div className="mobile-menuSearchBar">
              <div className="mobile-menuSearchBarContainer">
                <input type="text" placeholder={t('mobile.search')} />
                <Link to="/shop" onClick={closeMobileMenuAndScrollToTop}>
                  <FiSearch size={22} />
                </Link>
              </div>
            </div>
            <div className="mobile-menuList">
              <ul>
                <li>
                  <Link to="/" onClick={closeMobileMenuAndScrollToTop}>
                    {t('nav.home')}
                  </Link>
                </li>
                <li>
                  <Link to="/shop" onClick={closeMobileMenuAndScrollToTop}>
                    {t('nav.shop')}
                  </Link>
                </li>
                <li>
                  <Link to="/links/" onClick={toggleMobileMenu}>
                    {t('nav.links')}
                  </Link>
                </li>
                {user && (
                  <li onClick={handleLogout} style={{ cursor: 'pointer', color: '#c32929' }}>
                    {t('nav.logout')}
                  </li>
                )}
              </ul>
              <div className="mobile-menuLanguage" style={{ padding: "20px", borderTop: "1px solid #eee" }}>
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        </div>
      </nav>
      {/* Floating Cart (mobile only) */}
      {/* {showFloatingCart && (
        <Link to="/cart" className="floating-cart" aria-label="Cart" onClick={scrollToTop}>
          <Badge
            badgeContent={cart.items.length === 0 ? "0" : cart.items.length}
            color="primary"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <RiShoppingBagLine size={26} color="black" />
          </Badge>
        </Link>
      )} */}
    </>
  );
};

export default Navbar